import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Clock, Eye, 
  DollarSign, Gamepad2, Flag, UserX, RefreshCw, ChevronDown,
  ChevronUp, FileWarning, BadgeCheck, Ban
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status, type = "default" }) => {
  const statusConfig = {
    // Withdrawal statuses
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
    under_review: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Eye },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
    released: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: BadgeCheck },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    // Match verification statuses
    pending_verification: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
    opponent_confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Eye },
    verified: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
    // AI statuses
    passed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
    flagged: { bg: 'bg-red-500/20', text: 'text-red-400', icon: Flag },
    manual_review: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: Eye },
    // Match statuses
    disputed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
    awaiting_proof: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
  };
  
  const config = statusConfig[status] || { bg: 'bg-white/10', text: 'text-white/70', icon: Clock };
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const CompliancePanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [expandedSection, setExpandedSection] = useState('pending_withdrawals');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchComplianceQueue = async () => {
    try {
      const response = await axios.get(`${API}/admin/compliance/queue`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load compliance queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchComplianceQueue();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchComplianceQueue();
  };

  const handleWithdrawalAction = async (withdrawalId, action, notes = null) => {
    setActionLoading(withdrawalId);
    try {
      await axios.post(
        `${API}/admin/compliance/withdrawal/${withdrawalId}/review?action=${action}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Withdrawal ${action} successfully`);
      fetchComplianceQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMatchAction = async (matchId, action, winnerId = null, notes = null) => {
    setActionLoading(matchId);
    try {
      let url = `${API}/admin/compliance/match/${matchId}/verify?action=${action}`;
      if (winnerId) url += `&winner_id=${winnerId}`;
      if (notes) url += `&notes=${encodeURIComponent(notes)}`;
      
      await axios.post(url, {}, { withCredentials: true });
      toast.success(`Match ${action} successfully`);
      fetchComplianceQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAIStatusUpdate = async (matchId, status) => {
    setActionLoading(matchId);
    try {
      await axios.post(
        `${API}/admin/compliance/match/${matchId}/ai-status?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success('AI status updated');
      fetchComplianceQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  const sections = [
    {
      id: 'pending_withdrawals',
      title: 'Pending Withdrawals',
      icon: DollarSign,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      count: data?.counts?.pending_withdrawals || 0,
      items: data?.pending_withdrawals || []
    },
    {
      id: 'flagged_withdrawals',
      title: 'Flagged Withdrawals',
      icon: Flag,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      count: data?.counts?.flagged_withdrawals || 0,
      items: data?.flagged_withdrawals || []
    },
    {
      id: 'unverified_matches',
      title: 'Unverified Matches',
      icon: Gamepad2,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      count: data?.counts?.unverified_matches || 0,
      items: data?.unverified_matches || []
    },
    {
      id: 'disputed_matches',
      title: 'Disputed Matches',
      icon: AlertTriangle,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      count: data?.counts?.disputed_matches || 0,
      items: data?.disputed_matches || []
    },
    {
      id: 'ai_flagged_matches',
      title: 'AI Flagged Matches',
      icon: FileWarning,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      count: data?.counts?.ai_flagged_matches || 0,
      items: data?.ai_flagged_matches || []
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#00FF94]" />
          <h2 className="font-heading font-semibold text-xl">Compliance Verification Queue</h2>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          size="sm"
          disabled={refreshing}
          className="btn-secondary"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {sections.map(section => (
          <div 
            key={section.id}
            className={`p-4 rounded-xl ${section.bgColor} border ${section.borderColor} cursor-pointer transition-all hover:scale-105`}
            onClick={() => setExpandedSection(section.id)}
          >
            <div className="flex items-center gap-2 mb-2">
              <section.icon className={`w-5 h-5 ${section.color}`} />
              <span className={`text-2xl font-bold ${section.color}`}>{section.count}</span>
            </div>
            <p className="text-white/70 text-xs">{section.title}</p>
          </div>
        ))}
      </div>

      {/* Expandable Sections */}
      <div className="space-y-4">
        {sections.map(section => (
          <div key={section.id} className={`rounded-xl border ${section.borderColor} overflow-hidden`}>
            <button
              className={`w-full flex items-center justify-between p-4 ${section.bgColor} transition-colors`}
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
            >
              <div className="flex items-center gap-3">
                <section.icon className={`w-5 h-5 ${section.color}`} />
                <span className="font-medium">{section.title}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${section.bgColor} ${section.color}`}>
                  {section.count}
                </span>
              </div>
              {expandedSection === section.id ? (
                <ChevronUp className="w-5 h-5 text-white/50" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/50" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedSection === section.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-3 bg-black/20">
                    {section.items.length === 0 ? (
                      <p className="text-white/50 text-center py-4">No items in queue</p>
                    ) : (
                      section.items.map((item, index) => (
                        <div 
                          key={item.withdrawal_id || item.match_id || index}
                          className="p-4 rounded-lg bg-white/5 border border-white/10"
                        >
                          {/* Withdrawal Item */}
                          {item.withdrawal_id && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{item.user_name}</p>
                                  <p className="text-white/50 text-sm">{item.user_email}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[#00FF94] font-bold">${item.amount?.toFixed(2)}</p>
                                  <p className="text-white/50 text-xs">Net: ${item.net_amount?.toFixed(2)}</p>
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 text-xs">
                                <StatusBadge status={item.status} />
                                {item.duplicate_account_flag && (
                                  <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400 flex items-center gap-1">
                                    <UserX className="w-3 h-3" /> Duplicate Flag
                                  </span>
                                )}
                                {item.identity_verification_required && !item.identity_verified && (
                                  <span className="px-2 py-1 rounded-full bg-orange-500/20 text-orange-400">
                                    ID Required
                                  </span>
                                )}
                                {!item.email_verified && (
                                  <span className="px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                                    Email Unverified
                                  </span>
                                )}
                              </div>
                              
                              <div className="text-xs text-white/50">
                                <span>{item.payment_method?.toUpperCase()}: {item.payment_username}</span>
                              </div>
                              
                              <div className="flex gap-2 pt-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-xs"
                                  onClick={() => handleWithdrawalAction(item.withdrawal_id, 'approve')}
                                  disabled={actionLoading === item.withdrawal_id}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-xs"
                                  onClick={() => handleWithdrawalAction(item.withdrawal_id, 'reject', 'Rejected by admin')}
                                  disabled={actionLoading === item.withdrawal_id}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Reject
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                  onClick={() => handleWithdrawalAction(item.withdrawal_id, 'flag_fraud', 'Flagged for review')}
                                  disabled={actionLoading === item.withdrawal_id}
                                >
                                  <Flag className="w-3 h-3 mr-1" /> Flag
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* Match Item */}
                          {item.match_id && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{item.game_name}</p>
                                  <p className="text-white/50 text-xs">Match: {item.match_id}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[#00F0FF] font-bold">${item.wager_amount?.toFixed(2)}</p>
                                  <StatusBadge status={item.outcome_verification_status || item.status} />
                                </div>
                              </div>
                              
                              {item.ai_verification_status && (
                                <div className="flex items-center gap-2">
                                  <span className="text-white/50 text-xs">AI Status:</span>
                                  <StatusBadge status={item.ai_verification_status} />
                                </div>
                              )}
                              
                              {item.dispute_reason && (
                                <p className="text-red-400 text-xs bg-red-500/10 p-2 rounded">
                                  Dispute: {item.dispute_reason}
                                </p>
                              )}
                              
                              {item.ai_flagged_reason && (
                                <p className="text-orange-400 text-xs bg-orange-500/10 p-2 rounded">
                                  AI Flag: {item.ai_flagged_reason}
                                </p>
                              )}
                              
                              <div className="flex gap-2 pt-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-xs"
                                  onClick={() => handleMatchAction(item.match_id, 'verify')}
                                  disabled={actionLoading === item.match_id}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" /> Verify
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-xs"
                                  onClick={() => handleMatchAction(item.match_id, 'reject', null, 'Match rejected')}
                                  disabled={actionLoading === item.match_id}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Reject
                                </Button>
                                {item.ai_verification_status === 'flagged' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs"
                                    onClick={() => handleAIStatusUpdate(item.match_id, 'passed')}
                                    disabled={actionLoading === item.match_id}
                                  >
                                    <BadgeCheck className="w-3 h-3 mr-1" /> Clear Flag
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompliancePanel;
